import { Inject, Injectable, DOCUMENT } from "@angular/core";
import chroma from "chroma-js";
import { MenuServiceParameterizationResponse } from "../models/menu-service.model";

const BRAND_CSS_VARS = [
  "--primary-color",
  "--secondary-color",
  "--text-color",
  "--text-title-category",
  "--background-button",
  "--text-active-footer",
] as const;

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  constructor(@Inject(DOCUMENT) private document: Document) {}

  private get rootEl(): HTMLElement {
    return this.document.documentElement;
  }

  setVariable(name: string, value: string) {
    this.rootEl.style.setProperty(name, value);
    // Remove legacy overrides left on body by older theme logic.
    this.document.body.style.removeProperty(name);
  }

  removeVariable(name: string) {
    this.rootEl.style.removeProperty(name);
    this.document.body.style.removeProperty(name);
  }

  clearBrandColors() {
    BRAND_CSS_VARS.forEach((name) => this.removeVariable(name));
  }

  applyParameterizationColors(
    parameterization: MenuServiceParameterizationResponse | null | undefined,
  ) {
    if (this.isDarkModeClassActive()) {
      this.clearBrandColors();
      return;
    }

    if (!parameterization) {
      this.clearBrandColors();
      return;
    }

    const primary =
      parameterization.PrimaryColor ||
      (parameterization as { primaryColor?: string }).primaryColor;
    const secondary =
      parameterization.SecondaryColor ||
      (parameterization as { secondaryColor?: string }).secondaryColor;
    const text =
      parameterization.TextColor ||
      (parameterization as { textColor?: string }).textColor;

    if (primary?.length > 0) {
      this.setVariable("--primary-color", primary);
      this.setVariable("--background-button", primary);
      this.setVariable("--text-active-footer", primary);
    } else {
      this.removeVariable("--primary-color");
      this.removeVariable("--background-button");
      this.removeVariable("--text-active-footer");
    }

    if (secondary?.length > 0) {
      this.setVariable("--secondary-color", secondary);

      const titleColor = this.getContrastTextColor(secondary);
      if (titleColor) {
        this.setVariable("--text-title-category", titleColor);
      } else {
        this.removeVariable("--text-title-category");
      }
    } else {
      this.removeVariable("--secondary-color");
      this.removeVariable("--text-title-category");
    }

    if (text?.length > 0) {
      this.setVariable("--text-color", text);
    } else {
      this.removeVariable("--text-color");
    }
  }

  setDarkLightMode(primaryColor: string) {
    const $html = this.rootEl;
    const shouldEnableDark = !$html.classList.contains("dark-mode");
    $html.classList.toggle("dark-mode", shouldEnableDark);

    if (shouldEnableDark) {
      this.clearBrandColors();
      localStorage.setItem("pede-mais-garcom-theme", "dark-mode");
    } else {
      this.setVariable("--primary-color", primaryColor || "#004a8f");
      this.setVariable("--background-button", primaryColor || "#004a8f");
      this.setVariable("--text-active-footer", primaryColor || "#004a8f");
      localStorage.setItem("pede-mais-garcom-theme", "light-mode");
    }
  }

  isDarkModeClassActive(): boolean {
    return this.rootEl.classList.contains("dark-mode");
  }

  isDarkMode() {
    return this.isDarkModeClassActive();
  }

  getContrastTextColor(backgroundColor?: string | null): string | undefined {
    if (!backgroundColor) {
      return undefined;
    }

    try {
      const color = chroma(backgroundColor);
      return color.luminance() < 0.5
        ? color.brighten(3).hex()
        : color.darken(3).hex();
    } catch {
      return undefined;
    }
  }

  getSecondaryContrastTextColor(): string | undefined {
    const secondary = getComputedStyle(this.rootEl)
      .getPropertyValue("--secondary-color")
      .trim();

    if (!secondary) {
      return undefined;
    }

    try {
      const color = chroma(secondary);
      return color.luminance() < 0.5
        ? color.brighten(5).hex()
        : color.darken(3).hex();
    } catch {
      return undefined;
    }
  }
}
